import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Divider,
  Dropdown,
  Layout,
  MenuProps,
  Row,
  Space,
} from 'antd';
import { CHAT_URL } from 'api/common/config';
import { firebaseAuth } from 'api/common/firebase';
import CreatePurchaseOrderModal from 'app/components/CreatePurchaseOrderModal';
import ImportShipmentsByTrackingModal from 'app/components/ImportShipments/ImportShipmentsModal';
import NavigationBreadcrumb from 'app/design-system/Breadcrumbs';
import dayjs from 'dayjs';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { RootState } from 'state/reducer';
import { userLogout } from 'state/slices/user.slice';

import {
  createShipmentCtaClickEvent,
  messagesPageVisitEvent,
} from 'utils/analytics-events';

import { ReactComponent as BeeboltHubIcon } from '../../../assets/icons/beebolt-hub.svg';
import { ReactComponent as SubscribeBoltIcon } from '../../../assets/icons/subscribe-bolt-icon.svg';
import { ReactComponent as MessagesIcon } from '../../../assets/menuIcons/messages.svg';
import AppNotifications from './Notification';
import { beamsClient } from './Notification/pusher.utils';
import SearchInput from './SearchInput';
import './header.style.scss';

const MessagesButton: FC<{ navigateToMessagesCenter }> = ({
  navigateToMessagesCenter,
}) => {
  const user = useSelector((state: RootState) => state.user.user);
  console.log('user: ', user);

  return (
    <Button
      icon={<MessagesIcon />}
      type="text"
      onClick={navigateToMessagesCenter}
      className="messages-btn"
    />
  );
};
const renderCreateDropdownItems = (
  onCreateShipmentClick
): MenuProps['items'] => {
  return [
    {
      key: '1',
      label: (
        <Link
          className="create-dropdown-item"
          to={{ pathname: '/shipments/create' }}
          onClick={onCreateShipmentClick}
        >
          New shipment
        </Link>
      ),
    },
    {
      key: '2',
      label: <ImportShipmentsByTrackingModal />,
    },
    {
      key: '3',
      label: <CreatePurchaseOrderModal text="New Purchase Order" />,
    },
  ];
};

const Header: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);

  const onLogout = () => {
    const notificationClient = beamsClient();
    if (notificationClient) {
      notificationClient
        ?.stop()
        .then(() => dispatch(userLogout({})))
        .catch((error) => {
          console.log('error', error);
        });
    } else {
      dispatch(userLogout({}));
    }
  };

  const onCreateShipmentClick = () => {
    if (user) {
      createShipmentCtaClickEvent({
        user_id: user.id,
        email: user.email,
        company: user?.company?.name || '',
        company_id: user?.companyId,
      });
    }
  };

  const profileMenu: MenuProps = {
    items: [
      {
        key: 'profile',
        label: 'Profile',
        onClick: () => navigate('/profile'),
      },

      {
        key: 'settings',
        label: 'Settings',
        onClick: () => navigate('/settings'),
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: 'Sign out',
        onClick: onLogout,
      },
    ],
  };

  const navigateToMessagesCenter = () => {
    navigate('/messages');
    messagesPageVisitEvent({
      user_id: user?.id || '',
      email: user?.email || '',
      company: user?.company?.name || '',
      company_id: user?.companyId || '',
    });
  };
  const navigateToNavasChat = async () => {
    const currentIserUid = firebaseAuth.currentUser?.uid;
    if (!currentIserUid) return;

    const chatWindow = window.open(CHAT_URL, '_blank');

    const sendToken = () => {
      chatWindow?.postMessage(
        { type: 'CUSTOM_TOKEN', currentIserUid },
        new URL(CHAT_URL).origin
      );
    };

    setTimeout(sendToken, 4000);
  };

  return (
    <Layout.Header className="header-wrapper">
      <Row className="header-row" justify="space-between" align="middle">
        <NavigationBreadcrumb />

        {user?.isTrialUser && user?.currentPeriodEnd && (
          <Space className="trial-wrapper" size={40} align="center">
            <span className="trial-text">
              You&apos;re on free trial! Days remaining:{' '}
              <span className="days-remaining">
                {user?.currentPeriodEnd
                  ? dayjs(user.currentPeriodEnd).diff(dayjs(), 'day')
                  : 0}
              </span>
            </span>
            <Divider type="vertical" className="trial-divider" />
            <Button
              onClick={() => navigate('/pricing')}
              type="primary"
              className="external-button"
              shape="round"
              icon={<SubscribeBoltIcon style={{ verticalAlign: '-3px' }} />}
              data-chameleon="create-shipment-cta"
            >
              Subscribe
            </Button>
          </Space>
        )}

        <Space size={16} align="center" className="header-actions">
          <Button
            icon={
              <BeeboltHubIcon
                height={18}
                width={18}
                style={{ verticalAlign: '-4px' }}
              />
            }
            type="primary"
            className="external-button"
            onClick={navigateToNavasChat}
          >
            Ask Navas
          </Button>
          <Dropdown
            overlayClassName="create-dropdown"
            menu={{ items: renderCreateDropdownItems(onCreateShipmentClick) }}
            placement="bottom"
            arrow={{ pointAtCenter: true }}
            trigger={['click']}
          >
            <Button
              type="primary"
              shape="round"
              icon={<PlusOutlined />}
              data-chameleon="create-shipment-cta"
            >
              Create
            </Button>
          </Dropdown>
          <SearchInput />
          <MessagesButton navigateToMessagesCenter={navigateToMessagesCenter} />
          <AppNotifications />

          <Dropdown menu={profileMenu} trigger={['click']}>
            <Avatar icon={<UserOutlined />} size={40} src={user?.photo} />
          </Dropdown>
        </Space>
      </Row>
    </Layout.Header>
  );
};

export default Header;
